import {
  Box,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";
import { Check, Clear } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import vlCVXLogo from "../../../assets/cvxLogo.svg";
import ybLogo from "../../../assets/tokens/yb.png";
import { useStylesDark, useStylesLight } from "../../../Styles/BribesStyles";
import { tokenOptions } from "../tokenOptions";

const BribesRow = (props) => {
  const { bribe, userVotes, userReward } = props;
  const pool =
    bribe.poolName || bribe.gauge || bribe.tokens[0]?.pool || "Unknown Pool";
  const classesLight = useStylesLight();
  const classesDark = useStylesDark();
  const [classes, setClasses] = useState(classesDark);
  const [open, setOpen] = useState(false);

  // theme setting
  useEffect(() => {
    if (props.ctheme === "light") {
      setClasses(classesLight);
    } else {
      setClasses(classesDark);
    }
  }, [props.ctheme]);

  // Format USD values
  const usdFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Format perVlCvx with more precision for small values
  const vlcvxFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });

  // Format dollar amount for total incentives
  const formattedDollarAmount = usdFormatter.format(bribe.totalUsd || 0);

  // Format perVlCvx value with higher precision
  const formattedPerVlCvx = vlcvxFormatter.format(bribe.perVlCvx || 0);

  // Format votes value
  const formattedVotes = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(bribe.votes || 0);

  // Format total user's potential reward
  const formattedUserReward = userReward
    ? usdFormatter.format(userReward)
    : "$0.00";

  // Calculate per-token rewards based on token's proportion of total USD
  const calculateTokenReward = (token) => {
    if (!userReward || !bribe.totalUsd || bribe.totalUsd === 0) return "$0.00";
    const tokenProportion = token.dollarValue / bribe.totalUsd;
    const tokenReward = userReward * tokenProportion;
    return usdFormatter.format(tokenReward);
  };

  // Get token images for display
  const getTokenImage = (tokenAddress) => {
    if (!tokenAddress) return null;
    const token = tokenOptions.find(
      (option) => option.value.toLowerCase() === tokenAddress.toLowerCase()
    );
    return token
      ? React.cloneElement(token.img, {
          style: { width: "24px", borderRadius: "50%" },
          title: token.label,
        })
      : null;
  };

  // Get token labels for display
  const getTokenLabel = (tokenAddress) => {
    if (!tokenAddress) return null;
    const token = tokenOptions.find(
      (option) => option.value.toLowerCase() === tokenAddress.toLowerCase()
    );
    return token ? token.label : null;
  };

  // Get unique tokens to prevent duplicates
  const getUniqueTokens = (tokens) => {
    return tokens.reduce((uniqueTokens, currentToken) => {
      if (
        !uniqueTokens.some(
          (token) => token.tokenAddress === currentToken.tokenAddress
        )
      ) {
        uniqueTokens.push(currentToken);
      }
      return uniqueTokens;
    }, []);
  };

  return (
    <>
      <TableRow
        className={open ? classes.openedTableRow : classes.tableRow}
        onClick={() => {
          setOpen(!open);
        }}
        style={{ cursor: "pointer" }}
      >
        <TableCell
          component="th"
          scope="row"
          style={{
            borderRadius: "10px 0 0 10px",
            fontWeight: "500",
            whiteSpace: "normal",
            wordBreak: "break-word",
            minWidth: "80px",
          }}
          className={classes.tableCell}
        >
          {pool}
          {bribe.optedOut && (
            <Tooltip title="You opted out of Votium rewards on this gauge for this epoch, so your votes here are not eligible.">
              <Typography
                color="error"
                style={{ fontSize: "0.75em", fontWeight: 500, lineHeight: 1.3 }}
              >
                Not eligible
              </Typography>
            </Tooltip>
          )}
        </TableCell>
        <TableCell align="left" style={{ width: "25%", minWidth: "70px" }}>
          {getUniqueTokens(bribe.tokens).map((token, index) => (
            <span key={index} style={{ marginRight: "-4px" }}>
              {getTokenImage(token.tokenAddress)}
            </span>
          ))}
        </TableCell>
        {typeof window !== "undefined" &&
          window.location.pathname === "/yb" && (
            <TableCell
              align="left"
              className={classes.tableCell}
              style={{ width: "12%", minWidth: "70px" }}
            >
              {bribe.maxPerVote ? (
                <Tooltip
                  arrow
                  title="These deposits have a max reward per veYB"
                >
                  <Check />
                </Tooltip>
              ) : (
                <Tooltip
                  arrow
                  title="These deposits have no max reward per veYB"
                >
                  <Clear />
                </Tooltip>
              )}
            </TableCell>
          )}
        <TableCell
          align="left"
          className={classes.tableCell}
          style={{ width: "20%", minWidth: "70px" }}
        >
          <Typography style={{ fontWeight: "500" }}>
            {formattedDollarAmount}
          </Typography>
        </TableCell>
        <TableCell
          align="left"
          className={classes.tableCell}
          style={{ width: "17%", minWidth: "60px" }}
        >
          <Typography style={{ fontWeight: "500" }}>
            {formattedVotes}
          </Typography>
        </TableCell>
        <TableCell
          align="left"
          className={classes.tableCell}
          style={{ width: "18%", minWidth: "60px" }}
        >
          <Typography style={{ fontWeight: "500" }}>
            {formattedPerVlCvx}
          </Typography>
        </TableCell>
        <TableCell align="right" style={{ borderRadius: "0 10px 10px 0" }}>
          <IconButton aria-label="expand row" size="small">
            {open ? (
              <KeyboardArrowUp className={classes.tableCell} />
            ) : (
              <KeyboardArrowDown className={classes.tableCell} />
            )}
          </IconButton>
        </TableCell>
      </TableRow>

      {open && (
        <>
          <Box style={{ marginBottom: "0.15em" }}></Box>
          <TableRow className={classes.openedTableRow}>
            <TableCell
              style={{
                paddingBottom: 0,
                paddingTop: 0,
                borderRadius: "10px",
                padding: "16px",
              }}
              colSpan={8}
            >
              <Table
                size="small"
                aria-label="bribe tokens"
                style={{ width: "100%", tableLayout: "fixed" }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      className={classes.tableCell}
                      style={{
                        borderBottom: "none",
                        width: "50%",
                        padding: "8px 4px 8px 16px",
                      }}
                    >
                      Token
                    </TableCell>
                    {typeof window !== "undefined" &&
                      window.location.pathname === "/yb" && (
                        <TableCell
                          align="left"
                          style={{ borderBottom: "none", width: "20%" }}
                          className={classes.tableCell}
                        >
                          Max Reward/veYB
                        </TableCell>
                      )}
                    <TableCell
                      align="left"
                      style={{
                        borderBottom: "none",
                        width: "20%",
                        padding: "8px 4px",
                      }}
                      className={classes.tableCell}
                    >
                      USD Amount
                    </TableCell>
                    <TableCell
                      align="left"
                      style={{
                        borderBottom: "none",
                        width: "45%",
                        padding: "8px 16px 8px 4px",
                      }}
                      className={classes.tableCell}
                    >
                      {userVotes > 0 && props.currentUser
                        ? bribe.optedOut
                          ? "Reward eligibility"
                          : "Your Expected Reward"
                        : ""}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bribe.tokens.map((token, index) => (
                    <TableRow key={index}>
                      <TableCell
                        component="th"
                        scope="row"
                        className={classes.tableCell}
                        style={{
                          borderBottom: "none",
                          width: "35%",
                          padding: "8px 4px 8px 16px",
                        }}
                      >
                        <Grid
                          container
                          direction="row"
                          alignItems="center"
                          wrap="nowrap"
                        >
                          <Grid item style={{ marginRight: "10px" }}>
                            {getTokenImage(token.tokenAddress)}
                          </Grid>
                          <Grid
                            item
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            <Typography variant="body2" noWrap>
                              {Number(token.amount).toLocaleString(undefined, {
                                maximumFractionDigits: 6,
                              })}
                              {" $"}
                              {getTokenLabel(token.tokenAddress)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </TableCell>
                      {typeof window !== "undefined" &&
                        window.location.pathname === "/yb" && (
                          <TableCell
                            align="left"
                            className={classes.tableCell}
                            style={{ borderBottom: "none", width: "20%" }}
                          >
                            {token.maxPerVote
                              ? "$" + Number(token.maxPerVote).toLocaleString()
                              : "—"}
                          </TableCell>
                        )}
                      <TableCell
                        align="left"
                        className={classes.tableCell}
                        style={{
                          borderBottom: "none",
                          width: "20%",
                          padding: "8px 4px",
                        }}
                      >
                        {usdFormatter.format(token.dollarValue || 0)}
                      </TableCell>
                      {userVotes > 0 && props.currentUser && (
                        <TableCell
                          align="left"
                          className={classes.tableCell}
                          style={{
                            borderBottom: "none",
                            width: "45%",
                            padding: "8px 16px 8px 4px",
                          }}
                        >
                          {bribe.optedOut ? (
                            <Tooltip title="You opted out of Votium rewards on this gauge for this epoch.">
                              <Typography color="error">Not eligible</Typography>
                            </Tooltip>
                          ) : token.excluded &&
                            props.currentUser &&
                            token.excluded.includes(
                              props.currentUser.uid.toLowerCase()
                            ) ? (
                            <Tooltip title="Your address is excluded from this incentive">
                              <Typography color="error">Excluded</Typography>
                            </Tooltip>
                          ) : (
                            calculateTokenReward(token)
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {userVotes > 0 && props.currentUser && (
                    <TableRow>
                      <TableCell
                        colSpan={userVotes > 0 ? 3 : 2}
                        style={{ borderBottom: "none", paddingTop: "16px" }}
                        className={classes.tableCell}
                      >
                        <Box
                          style={{ marginTop: "10px", marginBottom: "10px" }}
                        >
                          <Typography
                            variant="body2"
                            style={{
                              fontWeight: "500",
                              alignItems: "center",
                              display: "flex",
                            }}
                          >
                            <strong>Your voting power on this gauge:</strong>{" "}
                            <img
                              src={
                                typeof window !== "undefined" &&
                                window.location.pathname === "/yb"
                                  ? ybLogo
                                  : vlCVXLogo
                              }
                              style={{
                                width: "16px",
                                marginLeft: "4px",
                                marginRight: "4px",
                                borderRadius: "50%",
                              }}
                              alt="vlCVX"
                            ></img>{" "}
                            {userVotes.toLocaleString()}{" "}
                          </Typography>
                          {bribe.optedOut ? (
                            <Typography
                              variant="body2"
                              color="error"
                              style={{ fontWeight: "500", marginTop: "4px" }}
                            >
                              Not eligible for rewards on this gauge.
                            </Typography>
                          ) : (
                            userReward > 0 && (
                              <Typography
                                variant="body2"
                                style={{ fontWeight: "500", marginTop: "4px" }}
                              >
                                <strong>Your expected total reward:</strong>{" "}
                                {formattedUserReward}
                              </Typography>
                            )
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableCell>
          </TableRow>
        </>
      )}
    </>
  );
};

export default BribesRow;
